import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, TextField, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FieldLabel } from '../UI';
import { DashboardController } from '../../../controller/dashboardController';
import { findAllTiposCalendarioAction } from '../../../store/Global/LoadSelectors/loadSelectors.saga';
import { toBrazilianDate } from '../../../helpers/utils/utils';

const MAX_LENGTH_DESCRICAO = 70;

/**
 * Dialogo de edicao de eventos do dia, acionado pelo icone de editar do
 * `CalendarioCard`. Reaproveita integralmente a logica real do
 * `PublicarEventoCalendarioDashboard`/`DashboardController`
 * (`findAllCalendarEventByDate`, `makeCalendarEvent`, `saveEventCalendar`) —
 * so a camada visual e MUI v5 direto (Select via `TextField select`,
 * `IconButton`), sem os componentes v4 `SelectorField`/`InputField`/`TrashButton`.
 * Ver docs/plano-calendario-card-botao-edicao.md.
 *
 * Eventos ja persistidos (`salvar.value` falso) sao somente leitura (campos
 * desabilitados, sem asterisco no label, sem botao de excluir) — apenas os
 * adicionados nesta sessao do dialogo sao editaveis/removiveis. Ao publicar,
 * o array completo (antigos + novos) e reenviado; o backend decide o upsert
 * por `idCalendario`.
 */
export default function EditarEventosCalendarioDialog({
  open, selecionado, onClose, onPublicado,
}) {
  const dispatch = useDispatch();
  const [eventos, setEventos] = useState([]);
  const [publicando, setPublicando] = useState(false);
  const montadoRef = useRef(true);

  const tiposCalendario = useSelector(states => states.selectors.tiposCalendario.selector);
  const carregandoTipos = useSelector(states => states.selectors.tiposCalendario.loading);

  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelado = false;
    DashboardController.findAllCalendarEventByDate(selecionado).then((novosEventos) => {
      if (montadoRef.current && !cancelado) setEventos(novosEventos);
    });
    return () => { cancelado = true; };
  }, [open, selecionado]);

  useEffect(() => {
    if (open && tiposCalendario.length === 0 && !carregandoTipos) {
      dispatch(findAllTiposCalendarioAction());
    }
  }, [open, tiposCalendario.length, carregandoTipos, dispatch]);

  function handleChangeField(idx, name) {
    return (event) => {
      const novosEventos = [...eventos];
      novosEventos[idx] = {
        ...novosEventos[idx],
        [name]: { ...novosEventos[idx][name], value: event.target.value },
      };
      setEventos(novosEventos);
    };
  }

  function handleFocusField(idx, name) {
    return () => {
      const novosEventos = [...eventos];
      novosEventos[idx] = {
        ...novosEventos[idx],
        [name]: { ...novosEventos[idx][name], error: false },
      };
      setEventos(novosEventos);
    };
  }

  function handleDeleteEvent(idx) {
    return () => setEventos(eventos.filter((_, i) => i !== idx));
  }

  function handleAddEvent() {
    setEventos([...eventos, DashboardController.makeCalendarEvent({ salvar: true })]);
  }

  async function onClickPublicar() {
    setPublicando(true);
    const response = await DashboardController.saveEventCalendar(selecionado, eventos);
    if (!montadoRef.current) return;
    setEventos(response.eventos);
    setPublicando(false);

    if (response.status === DashboardController.status.failure) {
      toast.error(response.message, { style: { width: '392px' } });
    } else if (response.status === DashboardController.status.success) {
      onPublicado();
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={publicando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Eventos de
        {' '}
        {toBrazilianDate(selecionado)}
      </DialogTitle>
      <DialogContent>
        <Button startIcon={<AddIcon />} onClick={handleAddEvent} disabled={publicando} sx={{ mb: 2 }}>
          Adicionar Evento
        </Button>

        {eventos.map((evento, idx) => {
          const editavel = evento.salvar.value;
          return (
            <Box
              key={evento.key.value}
              sx={{
                display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2,
              }}
            >
              <Box sx={{ flex: '0 0 40%' }}>
                <FieldLabel>{editavel ? 'Tipo*' : 'Tipo'}</FieldLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  disabled={!editavel || publicando}
                  value={evento.idTipoCalendario.value}
                  error={evento.idTipoCalendario.error}
                  helperText={evento.idTipoCalendario.error ? evento.idTipoCalendario.errorMessage : ''}
                  onChange={handleChangeField(idx, 'idTipoCalendario')}
                  onFocus={handleFocusField(idx, 'idTipoCalendario')}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {tiposCalendario.map(tipo => (
                    <MenuItem key={tipo.id} value={tipo.id}>{tipo.value}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: 1 }}>
                <FieldLabel>{editavel ? 'Título*' : 'Título'}</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  disabled={!editavel || publicando}
                  value={evento.descricao.value}
                  error={evento.descricao.error}
                  helperText={evento.descricao.error ? evento.descricao.errorMessage : ''}
                  onChange={handleChangeField(idx, 'descricao')}
                  onFocus={handleFocusField(idx, 'descricao')}
                  inputProps={{ maxLength: MAX_LENGTH_DESCRICAO, autoComplete: 'off' }}
                />
              </Box>
              <Box sx={{ pt: 3.5 }}>
                {editavel && (
                  <Tooltip title="Excluir Evento">
                    <IconButton size="small" onClick={handleDeleteEvent(idx)} disabled={publicando}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publicando}>Cancelar</Button>
        <Button onClick={onClickPublicar} variant="contained" disabled={publicando}>
          Publicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditarEventosCalendarioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  selecionado: PropTypes.instanceOf(Date).isRequired,
  onClose: PropTypes.func.isRequired,
  onPublicado: PropTypes.func.isRequired,
};
